import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './ProductPage.module.scss'
import { getShopProduct, getShopProducts } from '../../service/ShopService'
import { useCart } from '../../contexts/CartContext'
import { useAuth } from '../../contexts/AuthContext'
import ProductGallery from './components/ProductGallery'
import ProductInfo from './components/ProductInfo'
import ProductReviews from './components/ProductReviews'
import ProductSpecs from './components/ProductSpecs'
import Loader from '../../components/Loader/Loader'

const ProductPageNew = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addToCart, isInCart } = useCart()
  const { isAuthenticated, user } = useAuth()
  
  const [product, setProduct] = useState(null)
  const [reviews, setReviews] = useState([])
  const [relatedProducts, setRelatedProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [activeTab, setActiveTab] = useState('description') // description, specs, reviews
  const [isFavorite, setIsFavorite] = useState(false)

  useEffect(() => {
    if (id) {
      loadProduct()
      loadReviews()
      loadRelatedProducts()
    }
  }, [id])

  const loadProduct = async () => {
    try {
      setLoading(true)
      const response = await getShopProduct(id)
      setProduct(response.data)
      
      if (response.data.images && response.data.images.length > 0) {
        setSelectedImage(0)
      }
      
      // Проверяем, в избранном ли товар
      if (isAuthenticated) {
        checkFavoriteStatus()
      }
    } catch (err) {
      setError('Ошибка загрузки товара')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const loadReviews = async () => {
    try {
      // Reviews functionality not available in ShopService
      setReviews([])
    } catch (err) {
      console.error('Ошибка загрузки отзывов:', err)
    }
  }

  const loadRelatedProducts = async () => {
    try {
      const response = await getShopProducts({ category: product?.category, limit: 4 })
      setRelatedProducts(response.data.results || response.data || [])
    } catch (err) {
      console.error('Ошибка загрузки похожих товаров:', err)
    }
  }

  const checkFavoriteStatus = async () => {
    try {
      // Favorites functionality not available in ShopService
      setIsFavorite(false)
    } catch (err) {
      console.error('Ошибка проверки избранного:', err)
    }
  }

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, { quantity, type: 'product' })
      
      // Показываем уведомление
      const notification = document.createElement('div')
      notification.className = 'cart-notification'
      notification.textContent = `${product.name} добавлен в корзину`
      notification.style.cssText = `
        position: fixed;
        top: 90px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        z-index: 9999;
        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
      `
      document.body.appendChild(notification)
      setTimeout(() => {
        notification.remove()
      }, 3000)
    }
  }

  const handleFavoriteToggle = async () => {
    if (!isAuthenticated) {
      navigate('/auth')
      return
    }

    try {
      // Favorites functionality not available in ShopService
      setIsFavorite(!isFavorite)
    } catch (err) {
      console.error('Ошибка изменения избранного:', err)
    }
  }

  const handleReviewSubmit = async (reviewData) => {
    if (!isAuthenticated) {
      navigate('/auth')
      return
    }

    try {
      // Review functionality not available in ShopService
      console.log('Review would be submitted:', reviewData)
      await loadReviews() // Перезагружаем отзывы
    } catch (err) {
      console.error('Ошибка отправки отзыва:', err)
      throw err
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU').format(price)
  }

  const calculateDiscount = (originalPrice, salePrice) => {
    if (!originalPrice || !salePrice || originalPrice <= salePrice) return 0
    return Math.round(((originalPrice - salePrice) / originalPrice) * 100)
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка товара...</p>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className={styles.errorContainer}>
        <h2>Товар не найден</h2>
        <p>{error || 'Запрашиваемый товар не существует'}</p>
        <button onClick={() => navigate('/catalog')} className={styles.backButton}>
          Вернуться к каталогу
        </button>
      </div>
    )
  }

  const discount = calculateDiscount(product.original_price, product.price)

  return (
    <div className={styles.productPage}>
      <div className={styles.container}>
        {/* Хлебные крошки */}
        <div className={styles.breadcrumbs}>
          <span onClick={() => navigate('/')} className={styles.breadcrumbLink}>
            Главная
          </span>
          <span className={styles.separator}>/</span>
          <span onClick={() => navigate('/catalog')} className={styles.breadcrumbLink}>
            Каталог
          </span>
          <span className={styles.separator}>/</span>
          <span className={styles.breadcrumbCurrent}>{product.name}</span>
        </div>

        {/* Основная информация о товаре */}
        <div className={styles.productMain}>
          <ProductGallery
            images={product.images || [{ image: product.main_image || product.image }]}
            selectedImage={selectedImage}
            onImageSelect={setSelectedImage}
            productName={product.name}
          />

          <ProductInfo
            product={product}
            quantity={quantity}
            onQuantityChange={setQuantity}
            onAddToCart={handleAddToCart}
            onFavoriteToggle={setIsFavorite}
            isFavorite={isFavorite}
            isInCart={isInCart(product.id)}
            isAuthenticated={isAuthenticated}
            discount={discount}
            formatPrice={formatPrice}
          />
        </div>

        {/* Табы с дополнительной информацией */}
        <div className={styles.productTabs}>
          <div className={styles.tabsHeader}>
            <button
              className={`${styles.tab} ${activeTab === 'description' ? styles.active : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Описание
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'specs' ? styles.active : ''}`}
              onClick={() => setActiveTab('specs')}
            >
              Характеристики
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'reviews' ? styles.active : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Отзывы ({reviews.length})
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'description' && (
              <div className={styles.description}>
                <h3>Описание товара</h3>
                <div 
                  className={styles.descriptionText}
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
                
                {product.features && product.features.length > 0 && (
                  <div className={styles.features}>
                    <h4>Особенности:</h4>
                    <ul>
                      {product.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'specs' && (
              <ProductSpecs 
                product={product}
              />
            )}

            {activeTab === 'reviews' && (
              <ProductReviews
                product={product}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductPageNew